import { PaymentMethodApi, SubscriptionApi, BillingsApi } from '@api/index';

const defaultState = () => {
  return {
    // Subscription plans
    plans: [],
    billingCycle: 'monthly',

    // Payment methods
    paymentMethods: [],

    // Billing history
    billingHistory: [],

    // Loading states (only for specific operations, not general page loading)
    upgradeLoading: false,
    unsubscribeLoading: false,
    paymentMethodLoading: false,

    // General error state
    error: null
  };
};

export default {
  namespaced: true,
  state: defaultState(),

  mutations: {
    resetState(state) {
      Object.assign(state, defaultState());
    },

    // Plan related mutations
    setPlans(state, plans) {
      state.plans = plans;
    },
    setBillingCycle(state, cycle) {
      state.billingCycle = cycle;
    },
    markPlanAsCanceled(state, planId) {
      const index = state.plans.findIndex(p => p.id === planId);
      if (index !== -1) {
        state.plans[index] = {
          ...state.plans[index],
          is_canceled: true
        };
      }
    },
    clearCanceledStatus(state) {
      state.plans = state.plans.map(plan => ({
        ...plan,
        is_canceled: false
      }));
    },

    // Payment method related mutations
    setPaymentMethods(state, methods) {
      state.paymentMethods = methods;
    },

    // Billing history related mutations
    setBillingHistory(state, history) {
      state.billingHistory = history;
    },

    // Loading state mutations (only for operations that need them)
    setUpgradeLoading(state, loading) {
      state.upgradeLoading = loading;
    },
    setUnsubscribeLoading(state, loading) {
      state.unsubscribeLoading = loading;
    },
    setPaymentMethodLoading(state, loading) {
      state.paymentMethodLoading = loading;
    },

    // Error mutations
    setError(state, error) {
      state.error = error;
    },
    clearError(state) {
      state.error = null;
    }
  },

  actions: {
    resetState({ commit }) {
      commit('resetState');
    },

    /**
     * Clear any error messages
     */
    clearError({ commit }) {
      commit('setError', null);
    },

    /**
     * Request information about enterprise plan (sends email to admin)
     * @returns {Promise} The API response
     */
    async requestEnterpriseContact() {
      // Make API request to notify admins about enterprise plan interest
      // Error will be automatically propagated to the component
      return await SubscriptionApi.requestEnterpriseContact();
    },

    // Plan related actions
    async fetchSubscriptionPlans({ commit }) {
      commit('clearError');

      try {
        const response = await SubscriptionApi.getSubscription();
        const packagePlans = response.data.packagePlans;
        const transformedPlans = transformPlans(packagePlans);

        commit('setPlans', transformedPlans);
        return transformedPlans;
      } catch (error) {
        commit('setError', extractErrorMessage(error));
        throw error;
      }
    },

    setBillingCycle({ commit }, cycle) {
      commit('setBillingCycle', cycle);
    },

    async subscribeToPlan({ commit, dispatch }, { planId, stripe }) {
      commit('setUpgradeLoading', true);
      commit('clearError');

      try {
        // Call the API to pay for the selected plan
        const response = await SubscriptionApi.payForSubscriptionPlan(planId);

        // Check if 3DS authentication is required
        if (response?.data?.required3ds) {
          // Handle 3DS authentication
          const { paymentIntent, error } = await stripe.confirmCardPayment(response?.data?.required3ds.clientSecret);

          if (error) {
            // 3DS authentication failed
            throw new Error(error.message || 'Authentication failed. Please try again.');
          }

          await SubscriptionApi.confirmSubscriptionPayment(paymentIntent.id);
        }

        // Reset cancellation status in store
        commit('clearCanceledStatus');

        // Reset cancellation status in user object
        await dispatch('user/resetCancellationStatus', null, { root: true });

        // Refresh subscription data
        await dispatch('fetchSubscriptionPlans');

        return true;
      } catch (error) {
        console.error('Error upgrading plan:', error);
        commit('setError', extractErrorMessage(error));
        throw error;
      } finally {
        commit('setUpgradeLoading', false);
      }
    },

    async cancelSubscription({ commit, dispatch, rootGetters }) {
      commit('setUnsubscribeLoading', true);
      commit('clearError');

      try {
        // Call the API to cancel the subscription
        await SubscriptionApi.cancelSubscriptionPlan();

        // Get current plan
        const currentPlan = rootGetters['billing/currentPlan'];

        // Mark the plan as canceled in the store
        if (currentPlan) {
          commit('markPlanAsCanceled', currentPlan.id);
        }

        // Update user data in Vuex store to immediately show cancellation status
        await dispatch('user/setCancellationStatus', null, { root: true });

        // Refresh subscription data
        await dispatch('fetchSubscriptionPlans');

        return true;
      } catch (error) {
        console.error('Error unsubscribing from plan:', error);
        commit('setError', extractErrorMessage(error));
        throw error;
      } finally {
        commit('setUnsubscribeLoading', false);
      }
    },

    // Payment method related actions
    async fetchPaymentMethods({ commit }) {
      commit('clearError');

      try {
        const response = await PaymentMethodApi.getPaymentMethods();
        commit('setPaymentMethods', response.data.paymentMethods || []);
        return response.data.paymentMethods || [];
      } catch (error) {
        commit('setError', extractErrorMessage(error));
        throw error;
      }
    },

    async addPaymentMethod({ commit, dispatch }, { fullName, stripe, card, clientSecret }) {
      commit('setPaymentMethodLoading', true);
      commit('clearError');

      try {
        // Confirm card setup with Stripe
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: card,
            billing_details: { name: fullName }
          }
        });

        if (error) {
          throw new Error(error.message);
        }

        // Save payment method to backend
        await PaymentMethodApi.savePaymentMethod(setupIntent.payment_method);

        // Refresh payment methods list
        await dispatch('fetchPaymentMethods');

        return true;
      } catch (error) {
        console.error('Error adding payment method:', error);
        commit('setError', extractErrorMessage(error));
        throw error;
      } finally {
        commit('setPaymentMethodLoading', false);
      }
    },

    async removePaymentMethod({ commit, dispatch }, paymentMethodId) {
      commit('setPaymentMethodLoading', true);
      commit('clearError');

      try {
        await PaymentMethodApi.deletePaymentMethod(paymentMethodId);

        // Refresh payment methods list
        await dispatch('fetchPaymentMethods');

        return true;
      } catch (error) {
        console.error('Error removing payment method:', error);
        commit('setError', extractErrorMessage(error));
        throw error;
      } finally {
        commit('setPaymentMethodLoading', false);
      }
    },

    async setDefaultPaymentMethod({ commit, dispatch }, paymentMethodId) {
      commit('setPaymentMethodLoading', true);
      commit('clearError');

      try {
        await PaymentMethodApi.setDefaultPaymentMethod(paymentMethodId);

        // Refresh payment methods list
        await dispatch('fetchPaymentMethods');

        return true;
      } catch (error) {
        console.error('Error setting default payment method:', error);
        commit('setError', extractErrorMessage(error));
        throw error;
      } finally {
        commit('setPaymentMethodLoading', false);
      }
    },

    // Billing history related actions
    async fetchBillingHistory({ commit }) {
      commit('clearError');

      try {
        const response = await BillingsApi.getBilling();
        commit('setBillingHistory', response.data.billings || []);
        return response.data.billings || [];
      } catch (error) {
        commit('setError', extractErrorMessage(error));
        throw error;
      }
    },

    // Stripe setup intent for payment methods
    async createSetupIntent({ commit }) {
      commit('clearError');

      try {
        const response = await PaymentMethodApi.createSetupIntent();
        return response.data.client_secret || null;
      } catch (error) {
        // Silent fail - this is not critical for page rendering
        return null;
      }
    }
  },

  getters: {
    // Plans getters
    allPlans: state => state.plans || [],

    // Get the current plan - either the subscribed plan or the free plan
    currentPlan: state => {
      if (!state.plans || state.plans.length === 0) {
        return null;
      }

      const subscribedPlan = state.plans.find(plan => plan.is_subscribed);
      const freePlan = state.plans.find(plan => plan.interval === 'day');
      return subscribedPlan || freePlan || (state.plans.length > 0 ? state.plans[0] : null);
    },

    // Get plans filtered by billing cycle
    availablePlans: state => {
      if (!state.plans) {
        return [];
      }

      return state.plans.filter(plan => {
        // Filter based on billing cycle
        return plan.interval === (state.billingCycle === 'yearly' ? 'year' : 'month');
      });
    },

    isSubscriptionCanceled: (state, getters, rootState) => {
      const currentPlan = getters.currentPlan;
      const user = rootState.user?.user;

      return (user && user.billing && currentPlan && currentPlan.is_subscribed && user.billing.canceled_at !== null) || (currentPlan && currentPlan.is_canceled === true);
    },

    // Payment methods getters
    paymentMethods: state => state.paymentMethods,

    // Billing history getters
    billingHistory: state => state.billingHistory,

    // Loading states
    isPlansLoading: state => state.plansLoading,
    isPaymentMethodsLoading: state => state.paymentMethodsLoading,
    isBillingHistoryLoading: state => state.billingHistoryLoading,
    isUpgradeLoading: state => state.upgradeLoading,
    isUnsubscribeLoading: state => state.unsubscribeLoading,
    isPaymentMethodLoading: state => state.paymentMethodLoading,

    // Helper getters
    error: state => state.error,
    billingCycle: state => state.billingCycle,

    // Add a getter for the initial loading state
    isInitialLoading: state => state.initialLoading,

    // Update the isLoading getter to include individual loading states
    isAnyLoading: state =>
      state.initialLoading || state.plansLoading || state.paymentMethodsLoading || state.billingHistoryLoading || state.upgradeLoading || state.unsubscribeLoading || state.paymentMethodLoading
  }
};

// Helper function to transform plans from API format to component format
function transformPlans(packagePlans) {
  if (!packagePlans || packagePlans.length === 0) {
    return [];
  }

  return packagePlans.map(plan => {
    const isFree = plan.interval === 'day';
    const isBusiness = plan.plans.max_members === null && plan.plans.max_storage_size === null;

    // Determine plan type based on plan name (more reliable than price)
    const planName = plan.name.toLowerCase();
    const isStarter = !isFree && !isBusiness && planName.includes('starter');
    const isPro = !isFree && !isBusiness && planName.includes('pro');

    // Extract basic features for plan cards (summary)
    let summaryFeatures = [];
    let detailedFeatures = [];

    if (isFree) {
      summaryFeatures = [`${plan.plans.max_storage_size} storage`, `Up to ${plan.plans.max_members} user`];
      detailedFeatures = [`${plan.plans.max_storage_size} storage`, `Up to ${plan.plans.max_members} user`, 'Basic access to core features'];
    } else if (isBusiness) {
      summaryFeatures = ['Custom storage', 'Unlimited users'];
      detailedFeatures = [
        'Custom setup. Serious scale. One call away',
        'Custom storage',
        'Unlimited users',
        'Full access to all features — digital asset management, editing, video generation, social media publishing, and more',
        'Priority support and onboarding',
        'Dedicated account manager'
      ];
    } else if (isStarter) {
      summaryFeatures = [`${plan.plans.max_storage_size} storage`, `Up to ${plan.plans.max_members} users`];
      detailedFeatures = [
        'Just getting started? This one is for you.',
        `${plan.plans.max_storage_size} to stash your stuff`,
        `Up to ${plan.plans.max_members} users`,
        'Full access to all features — digital asset management, editing, video generation, social media publishing, and more',
        'Priority support and onboarding'
      ];
    } else if (isPro) {
      summaryFeatures = [`${plan.plans.max_storage_size} storage`, `Up to ${plan.plans.max_members} users`];
      detailedFeatures = [
        'More brains, more storage, more hustle',
        `${plan.plans.max_storage_size} of storage power`,
        `Up to ${plan.plans.max_members} users`,
        'Full access to all features — digital asset management, editing, video generation, social media publishing, and more',
        'Priority support and onboarding'
      ];
    } else {
      // Fallback for other plans
      summaryFeatures = [`${plan.plans.max_storage_size} storage`, `Up to ${plan.plans.max_members} users`];
      detailedFeatures = summaryFeatures;
    }

    return {
      id: plan.id,
      name: plan.name,
      label: plan.label,
      description: plan.description,
      price: {
        // For business plans, use null to indicate 'Talk to our team'
        amount: isBusiness ? null : plan.price,
        // Convert currency to uppercase
        currency: plan.currency ? plan.currency.toUpperCase() : null,
        period: plan.interval
      },
      features: summaryFeatures, // Basic features for plan cards
      detailedFeatures: detailedFeatures, // Full features for confirmation dialog
      discount: plan.discount ? `${plan.discount}%` : null,
      origin_price: plan.origin_price,
      isBusiness: isBusiness,
      isStarter: isStarter,
      isPro: isPro,
      interval: plan.interval,
      is_subscribed: plan.is_subscribed,
      is_canceled: false, // Default value
      stripe_reference: plan.stripe_reference,
      status: plan.status,
      is_popular: plan.is_popular
    };
  });
}

// Helper function to extract error messages
function extractErrorMessage(error) {
  const errorSources = [error.response?.data?.errors, error.errors, error.data?.errors];

  for (const errors of errorSources) {
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.length > 1 ? errors[1] : errors[0];
    }
  }

  return error.message || 'An error occurred';
}
